import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { TrendingUp, TrendingDown, Calendar } from 'lucide-react';

export default function PortfolioPerformanceChart({ 
  performanceData = [], 
  currentValue = 0,
  totalGainLoss = 0,
  totalGainLossPercent = 0
}) {
  const { t } = useLanguage();

  // Generate mock performance data if not provided
  const chartData = performanceData.length > 0 ? performanceData : generateMockData(currentValue);

  const formatCurrency = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toLocaleString();
  };

  const isPositive = totalGainLoss >= 0;

  return (
    <Card className="border-border/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            {t('Portfolio Performance', 'Utendaji wa Portfolio')}
          </span>
          <div className={`flex items-center gap-1 text-sm font-medium ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
            {isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
            {isPositive ? '+' : ''}{totalGainLossPercent.toFixed(2)}% {t('all time', 'wakati wote')}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="portfolioGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={isPositive ? "#22c55e" : "#ef4444"} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={isPositive ? "#22c55e" : "#ef4444"} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis 
                dataKey="date" 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString('en-US', { month: 'short' });
                }}
              />
              <YAxis 
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                tickFormatter={(value) => `${formatCurrency(value)}`}
                width={60}
              />
              <Tooltip 
                contentStyle={{ 
                  background: 'hsl(var(--card))', 
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  fontSize: '12px'
                }}
                formatter={(value) => [`TZS ${value.toLocaleString()}`, t('Value', 'Thamani')]}
                labelFormatter={(label) => new Date(label).toLocaleDateString()}
              />
              <Area 
                type="monotone" 
                dataKey="value" 
                stroke={isPositive ? "#22c55e" : "#ef4444"} 
                strokeWidth={2}
                fill="url(#portfolioGradient)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        {/* Performance Stats */}
        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-border/50">
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">
              {t('1 Month', '1 Mwezi')}
            </p>
            <p className={`text-sm font-semibold ${chartData.length > 1 ? (chartData[chartData.length-1].value > chartData[Math.max(0, chartData.length-5)].value ? 'text-green-600' : 'text-red-600') : 'text-muted-foreground'}`}>
              {chartData.length > 1 
                ? `${((chartData[chartData.length-1].value - chartData[Math.max(0, chartData.length-5)].value) / chartData[Math.max(0, chartData.length-5)].value * 100).toFixed(1)}%` 
                : '-'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">
              {t('3 Months', '3 Miezi')}
            </p>
            <p className={`text-sm font-semibold ${chartData.length > 1 ? (chartData[chartData.length-1].value > chartData[Math.max(0, chartData.length-13)].value ? 'text-green-600' : 'text-red-600') : 'text-muted-foreground'}`}>
              {chartData.length > 12 
                ? `${((chartData[chartData.length-1].value - chartData[Math.max(0, chartData.length-13)].value) / chartData[Math.max(0, chartData.length-13)].value * 100).toFixed(1)}%` 
                : '-'}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs text-muted-foreground mb-1">
              {t('All Time', 'Wakati Wote')}
            </p>
            <p className={`text-sm font-semibold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {isPositive ? '+' : ''}{totalGainLossPercent.toFixed(1)}%
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Generate mock performance data based on current value
function generateMockData(currentValue) {
  const data = [];
  const months = 12;
  const startValue = currentValue * 0.85; // Assume 15% growth over time
  
  for (let i = months; i >= 0; i--) {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    
    // Generate value with some randomness but trending upward
    const progress = (months - i) / months;
    const baseValue = startValue + (currentValue - startValue) * progress;
    const noise = baseValue * (Math.random() * 0.04 - 0.02); // +/- 2% noise
    
    data.push({
      date: date.toISOString().split('T')[0],
      value: Math.round(baseValue + noise)
    });
  }
  
  // Ensure last value matches current value
  if (data.length > 0) {
    data[data.length - 1].value = currentValue;
  }
  
  return data;
}
